import { Fragment, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { canViewPage } from '@cls/shared';
import { api } from '../../lib/api.js';
import { useAuth } from '../../features/auth/AuthProvider.js';
import { ImportWizardModal } from '../domain/ImportWizardModal.js';
import { UploadModal } from '../domain/UploadModal.js';

interface Props {
  mobOpen: boolean;
  onClose: () => void;
}

interface DashboardKpis {
  complianceRatio?: number;
  validCerts?: number;
  totalCerts?: number;
}

interface NavItem {
  /** Permission key passed to canViewPage. */
  page: string;
  icon: string;
  label: string;
  /** URL to navigate to. Defaults to `/{page}`. Set when the URL doesn't match the page key. */
  url?: string;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    label: 'Main',
    items: [
      { page: 'dashboard', icon: '⬡', label: 'Dashboard' },
      { page: 'projects', icon: '◈', label: 'Projects' },
      { page: 'customers', icon: '◎', label: 'Customers' },
      { page: 'contacts', icon: '◉', label: 'Contacts' },
    ],
  },
  {
    label: 'Workforce',
    items: [
      { page: 'workers', icon: '◐', label: 'Workers' },
      { page: 'training', icon: '◫', label: 'Training' },
      { page: 'vacancy', icon: '⚑', label: 'Vacancies' },
      { page: 'deploylog', icon: '📋', label: 'Deploy Records' },
      { page: 'bulkdeploy', icon: '🚀', label: 'Bulk Deploy' },
    ],
  },
  {
    label: 'Site Tools',
    items: [
      { page: 'qrscan', icon: '⬛', label: 'QR Scanner' },
      { page: 'signin', icon: '✅', label: 'Sign In/Out' },
      { page: 'docsign', icon: '✍', label: 'Document Sign' },
    ],
  },
  {
    label: 'Procurement',
    items: [
      { page: 'suppliers', icon: '◑', label: 'Suppliers' },
    ],
  },
  {
    label: 'Reports',
    items: [
      { page: 'compliance', icon: '📊', label: 'Compliance Report' },
      { page: 'calendar', icon: '🗓', label: 'Availability' },
      { page: 'timesheet', icon: '⏱', label: 'Timesheets' },
      { page: 'scheduledreports', icon: '📨', label: 'Scheduled Reports' },
    ],
  },
  {
    label: 'System',
    items: [
      { page: 'settings.users', icon: '👥', label: 'Users', url: '/users' },
      { page: 'settings', icon: '⚙', label: 'Settings' },
    ],
  },
];

export function Sidebar({ mobOpen, onClose }: Props) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [importOpen, setImportOpen] = useState(false);
  const [uploadEntity, setUploadEntity] = useState<string | null>(null);

  const current = pathname.split('/').filter(Boolean)[0] ?? 'dashboard';
  const show = (page: string) => !user || canViewPage(user, page);

  const { data: kpis } = useQuery({
    queryKey: ['dashboard', 'kpis'],
    queryFn: () => api.get<DashboardKpis>('/dashboard/kpis').then((r) => r.data),
    refetchInterval: 30_000,
    enabled: !!user && (user.role === 'Admin' || user.role === 'Manager'),
  });

  const progPct = kpis?.complianceRatio ?? 0;
  const progLbl = kpis?.totalCerts != null && kpis.totalCerts > 0 ? `${progPct}% certs valid` : '—';

  const renderItem = (it: NavItem) => {
    const url = it.url ?? '/' + it.page;
    const segment = url.replace(/^\//, '').split('/')[0] ?? '';
    return (
      <div
        key={it.page}
        className={`nav-item${current === segment ? ' active' : ''}`}
        data-page={it.page}
        onClick={() => { navigate(url); onClose(); }}
      >
        <span>{it.icon}</span>{it.label}
      </div>
    );
  };

  const visibleSections = NAV_SECTIONS
    .map((s) => ({ label: s.label, items: s.items.filter((i) => show(i.page)) }))
    .filter((s) => s.items.length > 0);

  return (
    <>
      <div id="sidebar-overlay" onClick={onClose} style={{ display: mobOpen ? 'block' : 'none' }}></div>
      <div id="sidebar" className={mobOpen ? 'mob-open open' : ''}>
        <div className="logo-area">
          <img src="/cls-logo.jpg" alt="CLS" style={{ display: 'block', width: 90, height: 'auto' }} />
          <div className="logo-sub">Construction CRM</div>
        </div>
        <nav>
          {visibleSections.map((s, i) => (
            <Fragment key={s.label}>
              <div className="nav-lbl" style={i > 0 ? { marginTop: 6 } : undefined}>{s.label}</div>
              {s.items.map(renderItem)}
            </Fragment>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            {user?.role === 'Admin' ? <button
              onClick={() => setImportOpen(true)}
              style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 10, padding: '2px 4px', borderRadius: 4, fontWeight: 600 }}
              title="Import data from CSV or Excel"
            >
              📂 Import Data
            </button> : <span />}
            <span id="save-indicator" style={{ fontSize: 9, color: 'var(--faint)' }}>Live</span>
          </div>
          <div className="lbl">Cert Compliance</div>
          <div className="prog-bar">
            <div className="prog-fill" id="sb-prog" style={{ width: `${progPct}%` }}></div>
          </div>
          <div style={{ fontSize: 10, color: 'var(--muted)' }} id="sb-prog-lbl">{progLbl}</div>
        </div>
      </div>
      {importOpen ? <ImportWizardModal onClose={() => setImportOpen(false)} onPick={(entity) => { setImportOpen(false); setUploadEntity(entity); }} /> : null}
      {uploadEntity ? <UploadModal entity={uploadEntity} onClose={() => setUploadEntity(null)} /> : null}
    </>
  );
}
